import * as Popover from '@radix-ui/react-popover';
import { addDays, format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import type { ClassNames, Matcher } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { cn } from '~/chords/lib/cn';
import { type VariantProps, variants } from '~/chords/lib/variants';

import { ChevronIcon } from '../ChevronIcon/ChevronIcon';
import { Icon } from '../Icon';
import { dayPickerClassNames } from './DatePicker.styles';

const TODAY = new Date();

TODAY.setHours(0, 0, 0, 0);

const FUTURE_LIMIT = addDays(TODAY, 365 * 10); // 10 years

const datePickerVariants = variants({
  slots: {
    field: cn(
      'relative isolate flex items-center gap-sm py-sm ps-md',
      'rounded-xs outline outline-1 transition-colors-with-outline duration-100 outline-emphasis-subtle',
      'bg-emphasis-default text-default has-[[data-date-picker-trigger]:hover]:bg-emphasis-strong',
    ),
    trigger: cn(
      'flex items-center text-start after:absolute after:inset-0 after:rounded-xs',
      'focus-visible:outline-none focus-visible:after:[outline-style:auto]',
    ),
    text: cn('text-body-sm leading-6'),
    clearButton: cn(
      'relative z-10 flex size-lg shrink-0 items-center justify-center',
      'rounded-full transition-colors duration-100 text-default hover:bg-emphasis-strong',
    ),
    trailingIcon: cn('pointer-events-none flex'),
  },
  variants: {
    variant: {
      default: {},
      warning: { field: cn('outline-warning-default') },
      error: { field: cn('outline-error-default') },
    },
    appearance: {
      dropdown: {
        field: cn('pe-sm'),
      },
      input: {
        field: cn('pe-md'),
        trigger: cn('flex-1'),
      },
    },
    isPlaceholder: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      appearance: 'input',
      isPlaceholder: true,
      class: { text: cn('text-subtle') },
    },
  ],
});

export interface DatePickerProps
  extends Pick<
    VariantProps<typeof datePickerVariants>,
    'variant' | 'appearance'
  > {
  value?: Date;
  onApply: (date: Date) => void;
  /**
   * When provided, a clear button is shown while a date is selected. Clicking
   * it closes the calendar and calls `onClear`; the parent must reset `value`.
   */
  onClear?: () => void;
  minDate?: Date;
  allowFutureDates?: boolean;
  placeholder?: string;
  highlightToday?: boolean;
}

export function DatePicker({
  onApply,
  onClear,
  value,
  minDate,
  allowFutureDates = true,
  placeholder = 'Select Date',
  highlightToday = false,
  variant = 'default',
  appearance = 'dropdown',
}: DatePickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const handleSelectDate = useCallback(
    (date: Date) => {
      onApply(date);
      setOpen(false);
    },
    [onApply],
  );

  const displayValue = value ? format(value, 'MMM d, yyyy') : placeholder;
  const isPlaceholder = !value;

  const disabledDates = useMemo(() => {
    const matchers: Matcher[] = [];

    if (minDate) {
      matchers.push({ before: minDate });
    }

    if (!allowFutureDates) {
      matchers.push({ after: TODAY });
    }

    return matchers;
  }, [minDate, allowFutureDates]);

  const classNames: Partial<ClassNames> = { ...dayPickerClassNames };

  if (highlightToday) {
    classNames.today = '[&>button]:underline [&>button]:underline-offset-4';
  }

  const chevronDirection = open ? 'up' : 'down';
  const { field, trigger, text, clearButton, trailingIcon } =
    datePickerVariants({
      variant,
      appearance,
      isPlaceholder,
    });

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div data-testid="date-picker-field" className={field()}>
          <Popover.Trigger asChild>
            <button
              type="button"
              data-testid="date-picker-trigger"
              data-date-picker-trigger=""
              aria-invalid={variant === 'error'}
              className={trigger()}
            >
              <span className={text()}>{displayValue}</span>
            </button>
          </Popover.Trigger>

          {onClear && value ? (
            <button
              type="button"
              data-testid="date-picker-clear"
              aria-label={t('datePicker.clearDate')}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              className={clearButton()}
            >
              <Icon type="xmark" size="sm" aria-hidden />
            </button>
          ) : null}

          <span aria-hidden className={trailingIcon()}>
            {appearance === 'dropdown' ? (
              <ChevronIcon direction={chevronDirection} />
            ) : null}

            {appearance === 'input' ? (
              <Icon type="calendar" variant="light" size="sm" />
            ) : null}
          </span>
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          className="z-10 mt-xs w-[18rem] rounded-sm border bg-level-03 border-emphasis-subtle"
        >
          <div className="flex">
            <DayPicker
              showOutsideDays
              required={true}
              mode="single"
              captionLayout="dropdown"
              disabled={disabledDates}
              startMonth={minDate || TODAY}
              endMonth={allowFutureDates ? FUTURE_LIMIT : TODAY}
              selected={value}
              onSelect={handleSelectDate}
              numberOfMonths={1}
              classNames={classNames}
              formatters={{
                formatWeekdayName: (weekday) => format(weekday, 'ccccc'),
                formatMonthDropdown: (month) => format(month, 'MMMM'),
              }}
              data-testid="date-picker-calendar"
            />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

DatePicker.displayName = 'DatePicker';
